import { CloseCircleOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Result, Typography } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import App from './app/App';
import { AppContext } from './AppContext';
import {
  BREAKPOINTS,
  ERROR_PAGE_SUBTITLE,
  ERROR_PAGE_TITLE,
  ROUTES,
} from './common/constants';
import LoaderComponent from './components/LoaderComponent';
import MaintenancePage from './components/MaintenancePage';
import history from './historyData';
import ForgetPassword from './modules/auth/ForgetPassword';
import { GET_CURRENT_USER } from './modules/auth/graphql/Queries';
import Login from './modules/auth/Login';
import Logout from './modules/auth/Logout';
import RefreshToken from './modules/auth/RefreshToken';
import ResetPassword from './modules/auth/ResetPassword';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const { Paragraph } = Typography;

const MyFallbackComponent = ({ error, componentStack }) => (
  <Result
    status="error"
    title={ERROR_PAGE_TITLE}
    subTitle={ERROR_PAGE_SUBTITLE}
  >
    <div className="desc">
      <Paragraph>
        <Typography.Title level={4}> Error:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {error?.message?.toString()}
      </Paragraph>
      <Paragraph>
        <Typography.Title level={4}> Stacktrace:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {componentStack}
      </Paragraph>
    </div>
  </Result>
);

const Routes = () => {
  const { initializeAuth, getToken, dispatch } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const idToken = getToken();

  const [getCurrentUser] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const currentUser = res?.currentUserProfile;
      initializeAuth(
        idToken,
        currentUser,
        currentUser?.permissions,
        currentUser?.refreshToken,
      );
      setLoading(false);
    },
    onError: () => {
      history?.push(ROUTES?.LOGOUT);
      setLoading(false);
    },
  });
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (idToken) {
        const res = await getCurrentUser();
        const currentUserProfile = res?.data?.currentUserProfile || {};
        if (
          currentUserProfile?.isActive === false ||
          currentUserProfile?.isArchived === true
        ) {
          history?.push(ROUTES?.LOGOUT);
        }
      } else {
        setLoading(false);
      }
    };
    fetchCurrentUser();

    // Below line is disabling Eslint auto fix we don't want any value in use effect array
    // We want to call initializeAuth once. Please add this line while you working with hooks and you want to call it once.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const updateMedia = () => {
      // eslint-disable-next-line no-undef
      if (window.innerWidth > BREAKPOINTS.tablet) {
        dispatch({
          type: 'SET_IS_DESKTOP',
          data: true,
        });
      } else {
        dispatch({
          type: 'SET_IS_DESKTOP',
          data: false,
        });
      }
    };

    // eslint-disable-next-line no-undef
    window.addEventListener('resize', updateMedia);
    // eslint-disable-next-line no-undef
    return () => window.removeEventListener('resize', updateMedia);
    // eslint-disable-next-line no-undef
  }, [window.innerWidth, window?.screen?.orientation?.type]);

  // use this variable from envs so that we can able to run maintenance page on runtime.
  const maintenance = process.env.REACT_APP_MAINTENANCE_ENABLE;

  if (loading) return <LoaderComponent />;

  return (
    <Sentry.ErrorBoundary fallback={MyFallbackComponent}>
      <Router history={history}>
        {maintenance === 'true' ? (
          <MaintenancePage />
        ) : (
          <Switch>
            <PublicRoute
              exact
              path={ROUTES?.FORGET_PASSWORD}
              component={ForgetPassword}
            />
            <PublicRoute exact path={ROUTES?.LOGIN} component={Login} />
            <Route exact path={ROUTES?.RESET} component={ResetPassword} />
            <PrivateRoute exact path={ROUTES?.LOGOUT} component={Logout} />
            <PrivateRoute
              exact
              path={ROUTES?.AUTHENTICATION}
              component={RefreshToken}
            />
            <PrivateRoute path={ROUTES?.MAIN} component={App} />
          </Switch>
        )}
      </Router>
    </Sentry.ErrorBoundary>
  );
};
export default Routes;
